import { ComponentProps, useState } from 'react';
import { DayClickEventHandler, DayPicker } from 'react-day-picker';

import { buttonVariants } from '../button';
import { CalendarCaption } from './calendar-caption';

import { cn } from '../../utils/cn';

type CalendarProps = ComponentProps<typeof DayPicker> & {
  yearsRange?: number;
};

function Calendar({ id, className, classNames, yearsRange = 10, ...props }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(props?.defaultMonth ?? new Date());
  const originalOnDayClick = props?.onDayClick;

  // Update dropdowns when a day is clicked
  const handleDayClick: DayClickEventHandler = (day, activeModifiers, e) => {
    if (day instanceof Date) {
      setCurrentMonth(new Date(day.getFullYear(), day.getMonth(), 1));
    }
    originalOnDayClick?.(day, activeModifiers, e);
  };

  return (
    <DayPicker
      fixedWeeks
      month={currentMonth}
      onDayClick={handleDayClick}
      onMonthChange={setCurrentMonth}
      showOutsideDays
      weekStartsOn={0}
      className={cn('h-[22.25rem] rounded-lg px-4 py-6 select-none', className)}
      classNames={{
        month: 'flex flex-col items-center gap-6',
        caption_label: 'hidden',
        head_row: 'flex',
        head_cell: 'text-muted-foreground w-10 font-normal text-xs',
        row: 'flex w-full mt-1.5',
        cell: cn(
          'relative mx-1 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-lg',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-lg [&:has(>.day-range-start)]:rounded-l-lg first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg'
            : '[&:has([aria-selected])]:rounded-lg',
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 p-0 font-normal aria-selected:opacity-100',
        ),
        day_range_start: 'day-range-start',
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent/50 text-accent',
        day_outside:
          'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Caption: (captionProps) => (
          <CalendarCaption
            baseId={id}
            displayMonth={captionProps.displayMonth}
            yearsRange={yearsRange}
            onChangeMonth={setCurrentMonth}
          />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
